import { emptyStep, type WorkflowFormState, type WorkflowBuilderStep } from '../pages/admin/WorkflowBuilderTypes.js';

type WorkflowAssistantPatch = Partial<WorkflowFormState> & {
  addSteps?: Array<Partial<WorkflowBuilderStep>>;
  stepUpdates?: WorkflowStepPatch[];
  updateSteps?: WorkflowStepPatch[];
  removeStepIds?: Array<string | number>;
  removeSteps?: Array<string | number>;
  contractUpdates?: WorkflowContractUpdate[];
  processorProposals?: WorkflowProcessorProposal[];
};

type WorkflowStepPatch = {
  id?: string | number;
  stepId?: string | number;
  name?: string;
  changes?: Partial<WorkflowBuilderStep>;
} & Partial<WorkflowBuilderStep>;

type WorkflowContractUpdate = {
  id?: string | number;
  stepId?: string | number;
  name?: string;
  outputContract?: WorkflowBuilderStep['outputContract'];
  inputContracts?: WorkflowBuilderStep['inputContracts'];
};

type WorkflowProcessorProposal = {
  name?: string;
  sourceStep?: string | number;
  sourceSteps?: Array<string | number>;
  targetStep?: string | number;
  type?: string;
  mode?: string;
  mappings?: Array<{ source: string; target: string; label?: string }>;
  conditions?: Array<Record<string, unknown>>;
  groupBy?: string;
  reason?: string;
};

type WorkflowContractMap = Record<string, NonNullable<WorkflowBuilderStep['outputContract']>>;

const TOP_LEVEL_FIELDS: Array<keyof WorkflowFormState> = [
  'name',
  'description',
  'category',
  'status',
  'tags',
  'cronSchedule',
  'scheduleEnabled',
  'flowDirection',
];

function stepMatches(step: WorkflowBuilderStep, ref: unknown): boolean {
  if (ref === null || ref === undefined || ref === '') return false;
  const value = String(ref);
  return step.id.toString() === value || step.name === value;
}

function normalizeStepUpdate(raw: WorkflowStepPatch): {
  ref: string | number | undefined;
  updates: Partial<WorkflowBuilderStep>;
} {
  const ref = raw.id ?? raw.stepId ?? raw.name;
  const updates = { ...(raw.changes ?? raw) } as Partial<WorkflowBuilderStep>;
  delete (updates as Record<string, unknown>).id;
  delete (updates as Record<string, unknown>).stepId;
  delete (updates as Record<string, unknown>).changes;
  return { ref, updates };
}

function makeStep(input: Partial<WorkflowBuilderStep>, order: number, index: number): WorkflowBuilderStep {
  return {
    ...emptyStep(order),
    ...input,
    id: input.id ?? -(Date.now() + index + 1),
    order: input.order ?? order,
    name: input.name ?? `Step ${order}`,
    templateSlug: input.templateSlug ?? null,
    pinnedTools: input.pinnedTools ?? [],
    executionMode: input.executionMode ?? 'sequential',
  };
}

function findStepIndex(steps: WorkflowBuilderStep[], ref: unknown): number {
  return steps.findIndex((step) => stepMatches(step, ref));
}

function normalizeContractUpdate(raw: WorkflowContractUpdate): {
  ref: string | number | undefined;
  updates: Partial<WorkflowBuilderStep>;
} {
  const ref = raw.id ?? raw.stepId ?? raw.name;
  const updates: Partial<WorkflowBuilderStep> = {};
  if (raw.outputContract !== undefined) updates.outputContract = raw.outputContract;
  if (raw.inputContracts !== undefined) updates.inputContracts = raw.inputContracts;
  if (Object.keys(updates).length > 0) {
    updates.contractNegotiationStatus = 'negotiated';
  }
  return { ref, updates };
}

function processorStepFromProposal(
  proposal: WorkflowProcessorProposal,
  steps: WorkflowBuilderStep[],
  index: number,
): { step: WorkflowBuilderStep; insertAt: number } | null {
  const type = proposal.type ?? 'field_map';
  const isLlmTransform = type === 'llm_transform' || proposal.mode === 'llm';
  if (!isLlmTransform && !['field_map', 'merge', 'filter', 'aggregate', 'normalize', 'dispatch'].includes(type))
    return null;
  const sourceSteps = Array.isArray(proposal.sourceSteps)
    ? proposal.sourceSteps.map((ref) => steps.find((step) => stepMatches(step, ref))?.name ?? String(ref))
    : undefined;
  const primarySourceRef = proposal.sourceStep ?? proposal.sourceSteps?.[0];
  const sourceIndex = findStepIndex(steps, primarySourceRef);
  const targetIndex = findStepIndex(steps, proposal.targetStep);
  const source = sourceIndex >= 0 ? steps[sourceIndex] : null;
  const target = targetIndex >= 0 ? steps[targetIndex] : null;
  const targetInputContracts = target?.inputContracts as WorkflowContractMap | null | undefined;
  const order = target?.order ?? (source ? source.order + 1 : steps.length + 1);
  const sourceName = source?.name || sourceSteps?.[0] || String(proposal.sourceStep ?? 'source');
  const sourceDisplayName = sourceSteps && sourceSteps.length > 0 ? sourceSteps.join(', ') : sourceName;
  const targetName = target?.name || String(proposal.targetStep ?? 'target');
  const inputContracts: WorkflowBuilderStep['inputContracts'] =
    sourceSteps && sourceSteps.length > 0
      ? (sourceSteps.reduce<WorkflowContractMap>((contracts, name) => {
          const matchingStep = steps.find((step) => step.name === name);
          if (matchingStep?.outputContract) contracts[matchingStep.name] = matchingStep.outputContract;
          return contracts;
        }, {}) as WorkflowBuilderStep['inputContracts'])
      : source?.outputContract
        ? { [sourceName]: source.outputContract }
        : null;

  if (isLlmTransform) {
    const step = makeStep(
      {
        name: proposal.name ?? `Transform ${sourceDisplayName} for ${targetName}`,
        description:
          proposal.reason ??
          `Semantic transform from ${sourceDisplayName} to ${targetName}. This step uses an LLM and must be reviewed before saving.`,
        templateSlug: 'data-transformer',
        templateId: null,
        executionMode: 'sequential',
        systemPromptOverride: [
          'Transform the selected upstream data into the target contract.',
          'Preserve meaning, cite assumptions, and do not invent fields.',
          proposal.reason ? `Reason this cannot be deterministic: ${proposal.reason}` : null,
        ]
          .filter(Boolean)
          .join('\n'),
        outputConfig: {
          llmTransform: {
            type: 'llm_transform',
            mode: 'llm',
            sourceStep: sourceName,
            ...(sourceSteps ? { sourceSteps } : {}),
            targetStep: targetName,
            reason: proposal.reason ?? null,
            approvalBoundary: 'authoring_time',
            redactionPolicy: 'structural_by_default',
            evaluation: { sample: true, validateOutputContract: true },
          },
        } as never,
        inputContracts,
        outputContract: targetInputContracts?.[sourceName] ?? target?.outputContract ?? null,
        contractNegotiationStatus: 'negotiated',
      },
      order,
      index,
    );
    return { step, insertAt: targetIndex >= 0 ? targetIndex : steps.length };
  }

  const step = makeStep(
    {
      name: proposal.name ?? defaultProcessorName(type, sourceDisplayName, targetName),
      description:
        proposal.reason ??
        `Deterministic ${type.replace('_', ' ')} processor between ${sourceDisplayName} and ${targetName}. Review before saving.`,
      templateSlug: null,
      templateId: null,
      executionMode: 'sequential',
      outputConfig: {
        processor: {
          type,
          mode: 'deterministic',
          sourceStep: sourceName,
          ...(sourceSteps ? { sourceSteps } : {}),
          targetStep: targetName,
          mappings: proposal.mappings ?? [],
          ...(proposal.conditions ? { conditions: proposal.conditions } : {}),
          ...(proposal.groupBy ? { groupBy: proposal.groupBy } : {}),
        },
      } as never,
      inputContracts,
      outputContract: targetInputContracts?.[sourceName] ?? null,
      contractNegotiationStatus: 'negotiated',
    },
    order,
    index,
  );
  return { step, insertAt: targetIndex >= 0 ? targetIndex : steps.length };
}

function defaultProcessorName(type: string, sourceName: string, targetName: string): string {
  if (type === 'field_map') return `Map ${sourceName} to ${targetName}`;
  if (type === 'merge') return `Merge ${sourceName}`;
  if (type === 'filter') return `Filter ${sourceName}`;
  if (type === 'aggregate') return `Aggregate ${sourceName}`;
  if (type === 'normalize') return `Normalize ${sourceName}`;
  if (type === 'dispatch') return `Dispatch ${sourceName}`;
  return `Process ${sourceName} to ${targetName}`;
}

export function applyWorkflowAssistantPatch(
  current: WorkflowFormState,
  patch: WorkflowAssistantPatch,
): WorkflowFormState {
  let next: WorkflowFormState = { ...current };

  for (const field of TOP_LEVEL_FIELDS) {
    if (field in patch && patch[field] !== undefined) {
      next = { ...next, [field]: patch[field] };
    }
  }

  let steps = [...next.steps];
  const removeRefs = [...(patch.removeStepIds ?? []), ...(patch.removeSteps ?? [])];
  if (removeRefs.length > 0) {
    steps = steps.filter((step) => !removeRefs.some((ref) => stepMatches(step, ref)));
  }

  const updates = [...(patch.stepUpdates ?? []), ...(patch.updateSteps ?? [])];
  if (updates.length > 0) {
    steps = steps.map((step) => {
      const matchingUpdates = updates.map(normalizeStepUpdate).filter(({ ref }) => stepMatches(step, ref));
      // One accumulator object instead of rebuilding the whole step on every iteration. The
      // result is identical — later patches still win — but it is a single copy, not N.
      if (matchingUpdates.length === 0) return step;
      const merged: WorkflowBuilderStep = { ...step };
      for (const { updates: stepPatch } of matchingUpdates) Object.assign(merged, stepPatch);
      return merged;
    });
  }

  const contractUpdates = patch.contractUpdates ?? [];
  if (contractUpdates.length > 0) {
    steps = steps.map((step) => {
      const matchingUpdates = contractUpdates.map(normalizeContractUpdate).filter(({ ref }) => stepMatches(step, ref));
      // Same single-copy merge as above. `contractVersion` still increments once per applied
      // update, since it is bumped inside the loop from the running value.
      if (matchingUpdates.length === 0) return step;
      const merged: WorkflowBuilderStep = { ...step };
      for (const { updates: stepPatch } of matchingUpdates) {
        const nextVersion = (merged.contractVersion ?? 0) + 1;
        Object.assign(merged, stepPatch, { contractVersion: nextVersion });
      }
      return merged;
    });
  }

  const processorProposals = patch.processorProposals ?? [];
  if (processorProposals.length > 0) {
    for (const [index, proposal] of processorProposals.entries()) {
      const candidate = processorStepFromProposal(proposal, steps, index);
      if (!candidate) continue;
      steps = steps.map((step) =>
        Number(step.order) >= Number(candidate.step.order) ? { ...step, order: Number(step.order) + 1 } : step,
      );
      steps.splice(candidate.insertAt, 0, candidate.step);
    }
    steps = steps.sort((a, b) => Number(a.order) - Number(b.order));
  }

  const additions = patch.addSteps ?? [];
  if (additions.length > 0) {
    const baseOrder = steps.reduce((max, step) => Math.max(max, Number(step.order) || 0), 0);
    steps = [...steps, ...additions.map((step, index) => makeStep(step, step.order ?? baseOrder + index + 1, index))];
  }

  return { ...next, steps };
}
